import React, { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { PARTICIPATION_PATHS } from '../../config/constants';
import { useAuth } from '../../providers/auth-provider';

function AssessmentTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip m-0 p-2">
        <div className="label">{`${payload[0].name} : ${payload[0].value} (${payload[0].payload.pct}%)`}</div>
      </div>
    );
  }

  return null;
}

function ClientDashboardAssessmentStatus({ assessmentsSummary, raters = false }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [totalAssessments, setTotalAssessments] = useState('0');
  const [completedAssessments, setCompletedAssessments] = useState('0');
  const [inProcessAssessments, setInProcessAssessments] = useState('0');
  const [notStartedAssessments, setNotStartedAssessments] = useState('0');
  const [chartSummaries, setChartSummaries] = useState();

  const goToParticipation = (filterReference) => {
    if (filterReference) {
      navigate(`/participation/${filterReference}`);
    } else {
      navigate('/participation');
    }
  };

  const getTitle = () => {
    if (auth.isSelect()) {
      return 'Assessment Status';
    } else if (!raters) {
      return 'Self-Assessment Status';
    } else {
      return 'Rater Assessment Status';
    }
  };

  const mouseEnter = (e) => {
    const event = { ...e };
    event.target.style.cursor = 'pointer';
  };

  const mouseLeave = (e) => {
    const event = { ...e };
    event.target.style.cursor = '';
  };

  const buildPieCell = (color, path) => {
    if (!raters) {
      return (
        <Cell
          fill={color}
          onClick={() => goToParticipation(path)}
          onMouseOver={(event) => mouseEnter(event)}
          onMouseOut={(event) => mouseLeave(event)}
        />
      );
    } else {
      return <Cell fill={color} />;
    }
  };

  const buildCountDiv = (count, path) => {
    if (count !== '0' && !raters) {
      return (
        <div
          className="text-larger text-bolder text-center"
          onClick={() => goToParticipation(path)}
          onMouseOver={(event) => mouseEnter(event)}
          onMouseOut={(event) => mouseLeave(event)}
        >
          {count}
        </div>
      );
    } else {
      return <div className="text-larger text-bolder text-center">{count}</div>;
    }
  };

  useEffect(() => {
    let orderedSummaries = [
      {
        name: 'Not started',
        count: 0,
        pct: 0,
      },
      {
        name: 'In Progress',
        count: 0,
        pct: 0,
      },
      {
        name: 'Completed',
        count: 0,
        pct: 0,
      },
    ];
    if (assessmentsSummary && assessmentsSummary.length) {
      let total = 0;
      assessmentsSummary.forEach((summary) => {
        switch (summary.status.toLowerCase()) {
          case 'completed':
            orderedSummaries[2].count = summary.count;
            break;
          case 'in progress':
            orderedSummaries[1].count = summary.count;
            break;
          default:
            orderedSummaries[0].count = summary.count;
        }
      });
      total = orderedSummaries[0].count + orderedSummaries[1].count + orderedSummaries[2].count;
      setTotalAssessments(total.toLocaleString());
      let pct = ((orderedSummaries[0].count / total) * 100).toFixed(0);
      orderedSummaries[0].pct = pct;
      pct = ((orderedSummaries[1].count / total) * 100).toFixed(0);
      orderedSummaries[1].pct = pct;
      pct = ((orderedSummaries[2].count / total) * 100).toFixed(0);
      orderedSummaries[2].pct = pct;
    } else {
      setTotalAssessments(0);
    }
    setCompletedAssessments(orderedSummaries[2].count.toLocaleString());
    setInProcessAssessments(orderedSummaries[1].count.toLocaleString());
    setNotStartedAssessments(orderedSummaries[0].count.toLocaleString());
    setChartSummaries(orderedSummaries);
  }, [assessmentsSummary, raters]);

  return (
    <>
      <div className="chart-title">{getTitle()}</div>
      {chartSummaries && (
        <Stack>
          <div className="row justify-content-center d-none d-xl-flex">
            <ResponsiveContainer width="80%" aspect="1.8">
              <PieChart>
                <Pie data={chartSummaries} dataKey="count" innerRadius={110} outerRadius={130}>
                  {buildPieCell('#e84613', PARTICIPATION_PATHS.NOT_STARTED)}
                  {buildPieCell('#ffd301', PARTICIPATION_PATHS.IN_PROGRESS)}
                  {buildPieCell('#00b050', PARTICIPATION_PATHS.COMPLETE)}
                </Pie>
                <Tooltip content={<AssessmentTooltip />} />
                <Legend verticalAlign="middle" layout="vertical" iconType="circle" iconSize={7} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="assessment-status-responsive row justify-content-center d-xl-none">
            <ResponsiveContainer className="assessment-status-pie-chart" width="90%" aspect="1">
              <PieChart>
                <Pie data={chartSummaries} dataKey="count" innerRadius={110} outerRadius={130}>
                  {buildPieCell('#e84613', PARTICIPATION_PATHS.NOT_STARTED)}
                  {buildPieCell('#ffd301', PARTICIPATION_PATHS.IN_PROGRESS)}
                  {buildPieCell('#00b050', PARTICIPATION_PATHS.COMPLETE)}
                </Pie>
                <Tooltip content={<AssessmentTooltip />} />
                <Legend verticalAlign="middle" layout="vertical" iconType="circle" iconSize={7} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="d-none d-lg-block">
            <Stack direction="horizontal" className="justify-content-evenly pt-5 pb-2" gap={3}>
              <div>
                {buildCountDiv(totalAssessments)}
                <div>Total Assessments</div>
              </div>
              <div>
                {buildCountDiv(completedAssessments, PARTICIPATION_PATHS.COMPLETE)}
                <div>Completed</div>
              </div>
              <div>
                {buildCountDiv(inProcessAssessments, PARTICIPATION_PATHS.IN_PROGRESS)}
                <div>In Progress</div>
              </div>
              <div>
                {buildCountDiv(notStartedAssessments, PARTICIPATION_PATHS.NOT_STARTED)}
                <div>Not Started</div>
              </div>
            </Stack>
          </div>
          <div className="d-md-block d-lg-none">
            <Stack direction="horizontal" gap={3} className="justify-content-evenly pt-1 pb-1">
              <div>
                {buildCountDiv(totalAssessments)}
                <div className="text-center">Total</div>
                <div>Assessments</div>
              </div>
              <div>
                {buildCountDiv(completedAssessments, PARTICIPATION_PATHS.COMPLETE)}
                <div>Completed</div>
                <div>&nbsp;</div>
              </div>
            </Stack>
            <Stack direction="horizontal" gap={3} className="justify-content-evenly pt-2 pb-4">
              <div>
                {buildCountDiv(inProcessAssessments, PARTICIPATION_PATHS.IN_PROGRESS)}
                <div>In Progress</div>
              </div>
              <div>
                {buildCountDiv(notStartedAssessments, PARTICIPATION_PATHS.NOT_STARTED)}
                <div>Not Started</div>
              </div>
            </Stack>
          </div>
        </Stack>
      )}
    </>
  );
}

export default ClientDashboardAssessmentStatus;
